import React, { useState, useEffect } from 'react';
import {
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  useTheme,
  useMediaQuery,
  Typography,
  CircularProgress,
  Alert,
  Tooltip,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  SelectChangeEvent,
  styled,
  Grid,
  Card,
  CardContent,
  Skeleton,
  Chip,
  Divider,
  Avatar,
  Badge,
  IconButton,
  Collapse,
  Stack
} from '@mui/material';
import { useQuery } from '@tanstack/react-query';
import { useAuth } from '../../../contexts/AuthContext';
import { getDersProgrami, getOgretmenDersProgrami } from '../../../services/api/dersProgramiService';
import { DersProgramiResponseModal } from '../../../types/modals';
import ScheduleIcon from '@mui/icons-material/Schedule';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import TodayIcon from '@mui/icons-material/Today';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import SchoolIcon from '@mui/icons-material/School';
import EventNoteIcon from '@mui/icons-material/EventNote';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';

// Styled components
const SeasonSelector = styled(FormControl)(({ theme }) => ({
  marginBottom: theme.spacing(3),
  marginTop: theme.spacing(2),
  '& .MuiOutlinedInput-root': {
    borderRadius: theme.shape.borderRadius * 1.5,
    '&.Mui-focused fieldset': {
      borderColor: theme.palette.primary.main,
      borderWidth: 2,
    },
  },
  '& .MuiInputLabel-root': {
    '&.Mui-focused': {
      color: theme.palette.primary.main,
    },
  },
}));

const StyledTableCell = styled(TableCell)(({ theme }) => ({
  fontWeight: 'bold',
  padding: theme.spacing(1.5),
  textAlign: 'center',
  [theme.breakpoints.down('md')]: {
    padding: theme.spacing(1),
  },
}));

const HeaderTableCell = styled(TableCell)(({ theme }) => ({
  backgroundColor: theme.palette.primary.main,
  color: theme.palette.common.white,
  fontWeight: 'bold',
  textAlign: 'center',
  padding: theme.spacing(1.5),
  [theme.breakpoints.down('md')]: {
    padding: theme.spacing(1),
  },
}));

const StyledTableRow = styled(TableRow)(({ theme }) => ({
  '&:nth-of-type(odd)': {
    backgroundColor: theme.palette.action.hover,
  },
  '&:hover': {
    backgroundColor: theme.palette.primary.light + '20',
    transition: 'background-color 0.3s ease',
  },
  transition: 'background-color 0.2s',
}));

const DayCard = styled(Card)(({ theme, active }: { theme: any, active: boolean }) => ({
  height: '100%',
  transition: 'transform 0.3s, box-shadow 0.3s',
  cursor: 'pointer',
  backgroundColor: active ? theme.palette.primary.light + '30' : theme.palette.background.paper,
  borderLeft: active ? `4px solid ${theme.palette.primary.main}` : 'none',
  '&:hover': {
    transform: 'translateY(-5px)',
    boxShadow: theme.shadows[6],
  },
}));

const ClassChip = styled(Chip)(({ theme }) => ({
  fontWeight: 'medium',
  transition: 'all 0.2s',
  '&:hover': {
    transform: 'scale(1.05)',
    boxShadow: theme.shadows[2],
  },
}));

const OgretmenDersProgramim: React.FC = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const isTablet = useMediaQuery(theme.breakpoints.down("md"));
  const { kullaniciModal } = useAuth();
  
  const [selectedSeason, setSelectedSeason] = useState<string>('2025');
  const availableSeasons = ['2023', '2024', '2025', '2026', '2027'];
  
  // For mobile view - selected day
  const [selectedDay, setSelectedDay] = useState<string>('pazartesi');
  const [expandedDay, setExpandedDay] = useState<string | null>(null);
  
  const handleSeasonChange = (event: SelectChangeEvent) => {
    setSelectedSeason(event.target.value);
  };
  
  const { data, isLoading, error, refetch } = useQuery({
    queryKey: ["OgretmenDersProgrami", kullaniciModal?.id, selectedSeason],
    queryFn: async () => {
      return await getOgretmenDersProgrami({
        kurumKodu: localStorage.getItem("kurumKodu") || "",
        ogretmenId: kullaniciModal?.id || "",
        sezon: parseInt(selectedSeason) + 1
      });
    },
    enabled: !!kullaniciModal?.id,
    retry: 1,
  });
  
  useEffect(() => {
    if (kullaniciModal?.id) {
      refetch();
    }
  }, [selectedSeason, refetch, kullaniciModal?.id]);
  
  // Günleri içeren başlık dizisi
  const days: (keyof DersProgramiResponseModal)[] = [
    "pazartesi",
    "sali",
    "carsamba",
    "persembe",
    "cuma",
    "cumartesi",
    "pazar",
  ];
  
  // Day name mapping for better display
  const dayNames: Record<string, string> = {
    pazartesi: "Pazartesi",
    sali: "Salı",
    carsamba: "Çarşamba",
    persembe: "Perşembe",
    cuma: "Cuma",
    cumartesi: "Cumartesi",
    pazar: "Pazar",
  };
  
  // Day icon mapping
  const dayIcons: Record<string, React.ReactNode> = {
    pazartesi: <TodayIcon />,
    sali: <TodayIcon />,
    carsamba: <TodayIcon />,
    persembe: <TodayIcon />,
    cuma: <TodayIcon />,
    cumartesi: <TodayIcon />,
    pazar: <TodayIcon />,
  };
  
  // Get class count for each day
  const getDayClassCount = (day: string) => {
    if (!data) return 0;
    return data.filter(ders => ders[day as keyof DersProgramiResponseModal] && ders[day as keyof DersProgramiResponseModal] !== '-').length;
  };
  
  // Toggle expanded day for mobile view
  const toggleDayExpand = (day: string) => {
    setExpandedDay(expandedDay === day ? null : day);
  };
  
  // Render skeletons for better UX during loading
  const renderSkeletons = () => {
    if (isTablet) {
      return (
        <Grid container spacing={2}>
          {days.map((day) => (
            <Grid item xs={12} sm={6} md={4} key={day}>
              <Card sx={{ height: '100%' }}>
                <CardContent>
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                    <Skeleton variant="circular" width={40} height={40} sx={{ mr: 2 }} />
                    <Skeleton variant="text" width={120} />
                  </Box>
                  <Divider sx={{ my: 1.5 }} />
                  {[1, 2, 3].map((item) => (
                    <Box key={item} sx={{ display: 'flex', alignItems: 'center', my: 1.5 }}>
                      <Skeleton variant="text" width={50} sx={{ mr: 2 }} />
                      <Skeleton variant="rectangular" height={32} width="100%" />
                    </Box>
                  ))}
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>
      );
    }
    
    return (
      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>
                <Skeleton variant="text" sx={{ bgcolor: 'rgba(255,255,255,0.3)' }} />
              </TableCell>
              {days.map((day) => (
                <TableCell key={day}>
                  <Skeleton variant="text" sx={{ bgcolor: 'rgba(255,255,255,0.3)' }} />
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {[1, 2, 3, 4, 5].map((row) => (
              <TableRow key={row}>
                <TableCell>
                  <Skeleton variant="text" />
                </TableCell>
                {days.map((day) => (
                  <TableCell key={day}>
                    <Skeleton variant="text" />
                  </TableCell>
                ))}
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    );
  };
  
  // Render card view for mobile and tablet
  const renderCardView = () => {
    return (
      <Grid container spacing={2}>
        {days.map((day) => (
          <Grid item xs={12} sm={6} md={4} key={day}>
            <DayCard 
              elevation={3}
              active={selectedDay === day}
              onClick={() => setSelectedDay(day)} theme={undefined}            >
              <CardContent>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    <Avatar sx={{ bgcolor: theme.palette.primary.main, mr: 2 }}>
                      {dayIcons[day]}
                    </Avatar>
                    <Typography variant="h6" component="h2">
                      {dayNames[day]}
                    </Typography>
                  </Box>
                  <Badge 
                    badgeContent={getDayClassCount(day)} 
                    color="secondary"
                    showZero
                    max={99}
                    sx={{ 
                      '& .MuiBadge-badge': { 
                        fontSize: '0.8rem', 
                        height: 22, 
                        minWidth: 22 
                      } 
                    }}
                  >
                    <SchoolIcon color="action" />
                  </Badge>
                </Box>
                
                <Divider sx={{ my: 1.5 }} />
                
                <Box sx={{ mt: 2 }}>
                  <Box 
                    sx={{ 
                      display: 'flex', 
                      justifyContent: 'space-between', 
                      alignItems: 'center',
                      mb: 1
                    }}
                    onClick={() => toggleDayExpand(day)}
                  >
                    <Typography variant="subtitle2" color="text.secondary">
                      Dersler
                    </Typography>
                    <IconButton size="small">
                      {expandedDay === day ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                    </IconButton>
                  </Box>
                  
                  <Collapse in={expandedDay === day || getDayClassCount(day) <= 3}>
                    {data?.map((ders, index) => (
                      ders[day] && ders[day] !== '-' && (
                        <Box 
                          key={index} 
                          sx={{ 
                            display: 'flex', 
                            alignItems: 'center', 
                            mb: 1.5,
                            p: 1,
                            borderRadius: 1,
                            bgcolor: theme.palette.action.hover
                          }}
                        >
                          <Chip 
                            label={ders.saat} 
                            size="small" 
                            color="primary"
                            variant="outlined"
                            icon={<AccessTimeIcon />}
                            sx={{ mr: 1.5, minWidth: 80 }}
                          />
                          <Typography variant="body2" fontWeight="medium">
                            {ders[day]}
                          </Typography>
                        </Box>
                      )
                    ))}
                    
                    {getDayClassCount(day) === 0 && (
                      <Alert 
                        severity="info" 
                        variant="outlined" 
                        icon={<EventNoteIcon />}
                        sx={{ mt: 1 }}
                      >
                        Bu gün için ders bulunmamaktadır.
                      </Alert>
                    )}
                  </Collapse>
                  
                  {!expandedDay && getDayClassCount(day) > 3 && (
                    <Box 
                      sx={{ 
                        textAlign: 'center', 
                        mt: 1, 
                        color: theme.palette.primary.main,
                        cursor: 'pointer'
                      }}
                      onClick={() => toggleDayExpand(day)}
                    >
                      <Typography variant="body2" sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                        Tümünü Gör <ExpandMoreIcon fontSize="small" />
                      </Typography>
                    </Box>
                  )}
                </Box>
              </CardContent>
            </DayCard>
          </Grid>
        ))}
      </Grid>
    );
  };
  
  // Render table view for desktop
  const renderTableView = () => {
    return (
      <TableContainer 
        component={Paper} 
        elevation={3} 
        sx={{ 
          minWidth: 900, 
          borderRadius: theme.shape.borderRadius * 1.5,
          overflow: 'hidden'
        }}
      >
        <Table>
          <TableHead>
            <TableRow>
              <HeaderTableCell>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  <AccessTimeIcon sx={{ mr: 1 }} />
                  Saat
                </Box>
              </HeaderTableCell>
              {days.map((day) => (
                <HeaderTableCell key={day}>
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    {dayIcons[day]}
                    <Box sx={{ ml: 1 }}>
                      {dayNames[day]}
                    </Box>
                  </Box>
                </HeaderTableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {data?.map((ders, index) => (
              <StyledTableRow key={index}>
                <StyledTableCell sx={{ fontWeight: 'bold' }}>
                  <Chip 
                    label={ders.saat}
                    size="small"
                    color="primary"
                    variant="outlined"
                    icon={<AccessTimeIcon fontSize="small" />}
                  />
                </StyledTableCell>
                {days.map((day) => (
                  <StyledTableCell key={day}>
                    {ders[day] && ders[day] !== '-' ? (
                      <ClassChip
                        label={ders[day]}
                        size="small"
                        color="default"
                        variant="filled"
                        sx={{ 
                          bgcolor: theme.palette.primary.light + '30',
                          color: theme.palette.text.primary,
                          fontWeight: 'medium',
                          minWidth: 100,
                          maxWidth: '100%',
                          height: 'auto',
                          '& .MuiChip-label': {
                            whiteSpace: 'normal',
                            padding: '6px 8px',
                          }
                        }}
                      />
                    ) : (
                      <Typography variant="body2" color="text.secondary">-</Typography>
                    )}
                  </StyledTableCell>
                ))}
              </StyledTableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    );
  };
  
  // Calculate stats
  const calculateStats = () => {
    if (!data) return { totalClasses: 0, daysWithClasses: 0, mostBusyDay: '', mostBusyDayCount: 0 };
    
    let daysCounts: Record<string, number> = {};
    let totalClasses = 0;
    
    days.forEach(day => {
      const count = getDayClassCount(day);
      daysCounts[day] = count;
      totalClasses += count;
    });
    
    const daysWithClasses = Object.values(daysCounts).filter(count => count > 0).length;
    
    let mostBusyDay = '';
    let mostBusyDayCount = 0;
    
    Object.entries(daysCounts).forEach(([day, count]) => {
      if (count > mostBusyDayCount) {
        mostBusyDay = day;
        mostBusyDayCount = count;
      }
    });
    
    return {
      totalClasses,
      daysWithClasses,
      mostBusyDay: dayNames[mostBusyDay] || '',
      mostBusyDayCount
    };
  };
  
  const stats = calculateStats();
  
  return (
    <Box sx={{ px: { xs: 1, sm: 2 } }}>
      <Box sx={{ 
        display: 'flex', 
        flexDirection: { xs: 'column', sm: 'row' },
        justifyContent: 'space-between',
        alignItems: { xs: 'flex-start', sm: 'center' },
        mb: 3
      }}>
        <Typography 
          variant="h5" 
          component="h1"
          sx={{ 
            fontWeight: 600, 
            mb: { xs: 2, sm: 0 },
            color: theme.palette.text.primary
          }}
        >
          <ScheduleIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
          Ders Programım
        </Typography>
        
        <SeasonSelector
          variant="outlined"
          size="small"
          sx={{ minWidth: 200 }}
        >
          <InputLabel id="season-select-label">Eğitim Sezonu</InputLabel>
          <Select
            labelId="season-select-label"
            id="season-select"
            value={selectedSeason}
            onChange={handleSeasonChange}
            label="Eğitim Sezonu"
          >
            {availableSeasons.map((season) => (
              <MenuItem key={season} value={season}>
                {season} - {(parseInt(season) + 1)} Eğitim Yılı
              </MenuItem>
            ))}
          </Select>
        </SeasonSelector>
      </Box>
      
      {/* Statistics Cards - Only show when data is available */}
      {!isLoading && !error && data && data.length > 0 && (
        <Grid container spacing={3} sx={{ mb: 3 }}>
          <Grid item xs={12} sm={4}>
            <Card elevation={3} sx={{ 
              height: '100%',
              transition: 'transform 0.3s, box-shadow 0.3s',
              '&:hover': {
                transform: 'translateY(-5px)',
                boxShadow: theme.shadows[6],
              },
            }}>
              <CardContent sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <Avatar sx={{ bgcolor: theme.palette.primary.main }}>
                  <SchoolIcon />
                </Avatar>
                <Box>
                  <Typography variant="h4" fontWeight="bold">{stats.totalClasses}</Typography>
                  <Typography variant="body2" color="text.secondary">Toplam Ders</Typography>
                </Box>
              </CardContent>
            </Card>
          </Grid>
          
          <Grid item xs={12} sm={4}>
            <Card elevation={3} sx={{ 
              height: '100%',
              transition: 'transform 0.3s, box-shadow 0.3s',
              '&:hover': {
                transform: 'translateY(-5px)',
                boxShadow: theme.shadows[6],
              },
            }}>
              <CardContent sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <Avatar sx={{ bgcolor: theme.palette.secondary.main }}>
                  <CalendarTodayIcon />
                </Avatar>
                <Box>
                  <Typography variant="h4" fontWeight="bold">{stats.daysWithClasses}</Typography>
                  <Typography variant="body2" color="text.secondary">Ders Olan Gün</Typography>
                </Box>
              </CardContent>
            </Card>
          </Grid>
          
          <Grid item xs={12} sm={4}>
            <Card elevation={3} sx={{ 
              height: '100%',
              transition: 'transform 0.3s, box-shadow 0.3s',
              '&:hover': {
                transform: 'translateY(-5px)',
                boxShadow: theme.shadows[6],
              },
            }}>
              <CardContent sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <Avatar sx={{ bgcolor: theme.palette.success.main }}>
                  <TodayIcon />
                </Avatar>
                <Box>
                  <Stack direction="row" alignItems="center" spacing={1}>
                    <Typography variant="h4" fontWeight="bold">{stats.mostBusyDayCount}</Typography>
                    <Chip 
                      label={stats.mostBusyDay} 
                      size="small" 
                      color="primary"
                      variant="outlined"
                      sx={{ height: 24 }}
                    />
                  </Stack>
                  <Typography variant="body2" color="text.secondary">En Yoğun Gün</Typography>
                </Box>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      )}
      
      {isLoading ? (
        <Box sx={{ mt: 3 }}>
          {renderSkeletons()}
        </Box>
      ) : error ? (
        <Alert
          severity="error"
          variant="filled"
          sx={{ 
            mt: 2, 
            borderRadius: theme.shape.borderRadius * 1.5,
            boxShadow: theme.shadows[3]
          }}
        >
          {(error as Error).message}
        </Alert>
      ) : !data?.length ? (
        <Alert 
          severity="info" 
          variant="outlined"
          icon={<CalendarTodayIcon />}
          sx={{ 
            mt: 2, 
            borderRadius: theme.shape.borderRadius * 1.5,
            boxShadow: theme.shadows[1],
            bgcolor: theme.palette.background.paper
          }}
        >
          <Typography variant="body1">
            {selectedSeason} - {parseInt(selectedSeason) + 1} eğitim sezonunda kayıtlı ders programı bulunmamaktadır.
          </Typography>
        </Alert>
      ) : (
        <Box sx={{ mt: { xs: 2, sm: 3 } }}>
          {isTablet ? renderCardView() : renderTableView()}
        </Box>
      )}
    </Box>
  );
};

export default OgretmenDersProgramim;